import * as ProductService from "@omni/services/product/product.service";
import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { Feature } from "@omni/types/api/product/bff/shared/feature.model";
import { FeatureKind } from "@omni/types/api/product/bff/shared/featureKind.model";
import { FeatureToolTipImage } from "@omni/types/api/product/bff/shared/featureToolTipImage.model";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { Translate } from "@omni/types/utils/translate";
import { isDefined } from "@omni/utils/helper.utils";
import { uniqBy } from "lodash-es";
import { JSX } from "react";

import { DamImage } from "@/utils/dam.utils";

import { LegalContacts } from "./sections/featureList/legalContacts/legalContacts.container";

const PRODUCT_ASSEMBLY_DOCUMENTS_KEY = "product.document.assembly";
const PRODUCT_CONDITIONS_DOCUMENTS_KEY = "product.document.conditions";
const PRODUCT_DELIVERY_DOCUMENTS_KEY = "product.document.delivery";
const PRODUCT_PARTS_DOCUMENTS_KEY = "product.document.parts";
const PRODUCT_PERFORMANCE_DOCUMENTS_KEY = "product.document.performance";
const PRODUCT_REPAIRABILITY_DOCUMENTS_KEY = "product.document.repairability";
const PRODUCT_RETURN_DOCUMENTS_KEY = "product.document.return";
const PRODUCT_SAFETY_DOCUMENTS_KEY = "product.document.safety";
const PRODUCT_SPARE1_DOCUMENTS_KEY = "product.document.spare1";
const PRODUCT_SPARE2_DOCUMENTS_KEY = "product.document.spare2";
const PRODUCT_WARRANTY_DOCUMENTS_KEY = "product.document.warranty";

const CODE_REFERENCES_AND_CODES = "CODE_REFERENCES_AND_CODES";

const FEATURE_KIND_BLACKLIST = ["energy"];

const FEATURE_LABEL_CODE_SUFFIXES = ["hubo_att_descriptivewarranty"];

export type ProductFeatureModel = {
    feature: Omit<
        Feature,
        | "id"
        | "code"
        | "unit"
        | "toolTipImage"
        | "kind"
        | "distinctiveFeature"
        | "label"
    > & {
        code?: string;
        unit?: {
            code: string;
            name: string;
            symbol: string;
        } | null;
        toolTipImage?: FeatureToolTipImage | null;
        kind?: FeatureKind;
    };
    tooltip?: {
        title?: string;
        component: () => JSX.Element;
    };
};

export const getSortedFeatureKinds = (
    product: Product<ProductFieldSet.Full>,
    t: Translate
) => {
    const referencesAndCodesFeatureKind = {
        sortOrder: 99,
        name: t("productDetail.featureList.title.referencesAndCodes"),
        code: CODE_REFERENCES_AND_CODES
    };

    if (ProductService.isAdditionalService(product)) {
        return [referencesAndCodesFeatureKind];
    }

    const sortedFeatureKinds = getSortedFeatures(product.features).map(
        (f) => f.kind
    );

    sortedFeatureKinds.push(referencesAndCodesFeatureKind);

    return uniqBy(sortedFeatureKinds, (f) => f.code);
};

const hasCompleteFeatureValue = (feature: Feature) => {
    return (
        !!feature.featureValues &&
        feature.featureValues.some((fv) => !!fv.formattedValue)
    );
};

const hasValidFeatureKind = (feature: Feature) => {
    return (
        !!feature.kind &&
        !!feature.kind.code &&
        !!feature.kind.name &&
        !FEATURE_KIND_BLACKLIST.some((f) => feature.code === f)
    );
};

const getSortedFeatures = (features: Feature[]) => {
    return features.filter(
        (f) => !!f.code && hasCompleteFeatureValue(f) && hasValidFeatureKind(f)
    );
};

export const hasDocuments = (product: Product<ProductFieldSet.Full>) => {
    let hasAnyDocuments = false;
    const docMap = getAllDocuments(product);
    for (const [_, value] of docMap) {
        if (!hasAnyDocuments) {
            hasAnyDocuments = value.length > 0;
        }
    }
    return hasAnyDocuments;
};

export const getAllDocuments = (product: Product<ProductFieldSet.Full>) => {
    const docMap = new Map<string, Array<Media>>();

    addDocuments(
        docMap,
        PRODUCT_ASSEMBLY_DOCUMENTS_KEY,
        product.assemblyDocuments
    );
    addDocuments(
        docMap,
        PRODUCT_CONDITIONS_DOCUMENTS_KEY,
        product.conditionsDocuments
    );
    addDocuments(
        docMap,
        PRODUCT_DELIVERY_DOCUMENTS_KEY,
        product.deliveryDocuments
    );
    addDocuments(docMap, PRODUCT_PARTS_DOCUMENTS_KEY, product.partsDocuments);
    addDocuments(
        docMap,
        PRODUCT_PERFORMANCE_DOCUMENTS_KEY,
        product.performanceDocuments
    );
    addDocuments(
        docMap,
        PRODUCT_REPAIRABILITY_DOCUMENTS_KEY,
        product.repairabilityDocuments
    );
    addDocuments(docMap, PRODUCT_RETURN_DOCUMENTS_KEY, product.returnDocuments);
    addDocuments(docMap, PRODUCT_SAFETY_DOCUMENTS_KEY, product.safetyDocuments);
    addDocuments(docMap, PRODUCT_SPARE1_DOCUMENTS_KEY, product.spare1Documents);
    addDocuments(docMap, PRODUCT_SPARE2_DOCUMENTS_KEY, product.spare2Documents);
    addDocuments(
        docMap,
        PRODUCT_WARRANTY_DOCUMENTS_KEY,
        product.warrantyDocuments
    );

    return docMap;
};

const addDocuments = (
    docMap: Map<string, Array<Media>>,
    key: string,
    documents?: Array<Media> | null
) => {
    const filterdDocs = documents?.filter((doc) => isDefined(doc.url)) || [];

    docMap.set(key, filterdDocs);
};

const isFeatureLabel = (feature: Feature) => {
    const hasLabel =
        feature.featureValues.length > 0 &&
        !!feature.featureValues.at(0)?.thumbnailURL;

    if (!hasLabel) return false;

    return (
        (feature.label && feature.distinctiveFeature) ||
        FEATURE_LABEL_CODE_SUFFIXES.some((suffix) =>
            feature.code.endsWith(suffix)
        )
    );
};

export const getFeatureLabels = (product: Product<ProductFieldSet.Full>) => {
    const featureLabels: Array<{ imageUrl?: string; altText?: string | null }> =
        [];

    if (product.fsc) {
        featureLabels.push({
            imageUrl: DamImage.icon.label.fsc(),
            altText: "productDetail.featurelabels.fsc.altText"
        });
    }

    if (product.pefc) {
        featureLabels.push({
            imageUrl: DamImage.icon.label.pefc(),
            altText: "productDetail.featurelabels.pefc.altText"
        });
    }

    if (product.recupel > 0) {
        featureLabels.push({
            imageUrl: DamImage.icon.label.recupel(),
            altText: "productDetail.featurelabels.recupel.altText"
        });
    }

    if (product.bebat > 0) {
        featureLabels.push({
            imageUrl: DamImage.icon.label.bebat(),
            altText: "productDetail.featurelabels.bebat.altText"
        });
    }

    product.warrantyLabels.forEach((label) =>
        featureLabels.push({
            imageUrl: label.url,
            altText: label.altText
        })
    );

    product.qualityLabels.forEach((label) =>
        featureLabels.push({
            imageUrl: label.url,
            altText: label.altText
        })
    );

    product.features
        .filter(isFeatureLabel)
        .map((f) => ({
            imageUrl: f.featureValues.at(0)?.thumbnailURL || undefined,
            altText: f.featureValues.at(0)?.description || undefined
        }))
        .forEach((l) => featureLabels.push(l));

    return featureLabels;
};

const groupFeaturePerFeatureKind = (
    featureMap: Map<string, Array<Partial<ProductFeatureModel>>>,
    feature: Feature
) => {
    if (feature.kind) {
        const currentFeatures = featureMap.get(feature.kind.code);

        if (currentFeatures) {
            currentFeatures.push({ feature });
        } else {
            featureMap.set(feature.kind.code, [{ feature }]);
        }
    }
};

const getFeaturesReferencesAndCodes = (
    product: Product<ProductFieldSet.Full>,
    t: Translate
) => {
    const isAdditionalService = ProductService.isAdditionalService(product);

    const featureList: Array<ProductFeatureModel> = [];

    featureList.push({
        feature: {
            name: t("productDetail.featureList.referencesAndCodes.sku"),
            featureValues: [{ formattedValue: product.sku }],
            coreFeature: false
        }
    });

    if (!isAdditionalService) {
        featureList.push({
            feature: {
                name: t("productDetail.featureList.referencesAndCodes.ean"),
                featureValues: [{ formattedValue: product.ean }],
                coreFeature: false
            }
        });
    }

    featureList.push({
        feature: {
            name: t(
                "productDetail.featureList.referencesAndCodes.supplierProductCode"
            ),
            featureValues: [{ formattedValue: product.supplierProductCode }],
            coreFeature: false
        }
    });

    if ((product.legalContacts || []).length > 0) {
        featureList.push({
            feature: {
                name: t("productDetail.featureList.referencesAndCodes.gpsr"),
                featureValues: [
                    {
                        formattedValue: t(
                            "productDetail.featureList.referencesAndCodes.gpsr.value"
                        )
                    }
                ],
                coreFeature: false
            },
            tooltip: {
                component: () => (
                    <div>
                        <LegalContacts contacts={product.legalContacts} />
                    </div>
                )
            }
        });
    }

    return featureList;
};

export const getFeaturesGroupedPerKind = (
    product: Product<ProductFieldSet.Full>,
    t: Translate
) => {
    const featureMap = new Map<string, Array<ProductFeatureModel>>();
    const sortedFeatures = getSortedFeatures(product.features);

    sortedFeatures.forEach((feature) =>
        groupFeaturePerFeatureKind(featureMap, feature)
    );

    featureMap.set(
        CODE_REFERENCES_AND_CODES,
        getFeaturesReferencesAndCodes(product, t)
    );

    return featureMap;
};
